// src/pages/admin/CourseLectures/lectures/Lecture05Unterlagenkontrolle.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Folder structure visual ───────────────────────────────────────
const FolderTree: React.FC = () => (
  <div className="bg-gray-900 rounded-xl p-4 font-mono text-xs">
    <div className="flex items-center gap-2 text-yellow-400 font-bold mb-2">
      <span>📁</span>
      <span>Bewerbungen_2026</span>
    </div>
    <div className="pl-5 space-y-1.5">
      {[
        { name: "2026-02-07_HEK_KaufmannGesundheitswesen", example: true },
        { name: "2026-03-01_Bosch_MechatronikerAusbildung", example: false },
        { name: "2026-03-15_REWE_KaufmannEinzelhandel", example: false },
      ].map((folder, i) => (
        <div key={i}>
          <div
            className={`flex items-center gap-2 ${folder.example ? "text-[#009fe3]" : "text-gray-400"}`}
          >
            <span>📂</span>
            <span>{folder.name}</span>
            {folder.example && (
              <span className="text-green-400 text-[10px]">← Beispiel</span>
            )}
          </div>
          {folder.example && (
            <div className="pl-6 mt-1 space-y-0.5 text-gray-500">
              {[
                "Stellenanzeige.pdf",
                "Anzeige_Link.txt",
                "Lebenslauf_v1.pdf",
                "Anschreiben.pdf",
                "Komplett_Bewerbung.pdf",
              ].map((f, j) => (
                <div key={j} className="flex items-center gap-1.5">
                  <span className="text-gray-600">📄</span>
                  <span
                    className={
                      f === "Komplett_Bewerbung.pdf" ? "text-green-400" : ""
                    }
                  >
                    {f}
                  </span>
                  {f === "Komplett_Bewerbung.pdf" && (
                    <span className="text-green-400 text-[10px]">
                      ← Anzeige+LL+Anschreiben
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ── Document checklist card ───────────────────────────────────────
const DocCheckCard: React.FC<{
  title: string;
  icon: string;
  items: string[];
  color: string;
}> = ({ title, icon, items, color }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-lg">{icon}</span>
      <span className="text-sm font-bold">{title}</span>
    </div>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-1.5 text-xs">
          <span className="flex-shrink-0 mt-0.5 opacity-60">›</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture05Unterlagenkontrolle: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 04 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "1 Stellenanzeige ausgesucht",
            "Anschreiben nach DIN 5008 geschrieben",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Qualitätscheck — Zeugnisse" },
          { num: "2", label: "Qualitätscheck — Lebenslauf & Keywords" },
          { num: "3", label: "Portal-Sprung — Direkt zur Quelle" },
          { num: "4", label: "Portal-Sprung — Der Trick" },
          { num: "5", label: "Anschreiben mit G2G generieren" },
          { num: "6", label: "Digitale Ablage — Ordnerstruktur" },
          { num: "7", label: "Digitale Ablage — Ordnerinhalt" },
        ]}
      />

      {/* 01 — Qualitätscheck Zeugnisse */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="📋"
        title={"Qualitätscheck — Zeugnisse & Nachweise"}
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Bevor du dich bewirbst: Sind alle deine Unterlagen wirklich bereit?
        </p>
        <div className="space-y-3">
          <DocCheckCard
            title="Saubere PDFs"
            icon="📄"
            color="bg-blue-50 border-blue-200 text-blue-800"
            items={[
              "Sind alle Dokumente als saubere, lesbare PDFs vorhanden?",
              "Keine unscharfen Scans, keine abgeschnittenen Ränder",
            ]}
          />
          <DocCheckCard
            title="Original & Übersetzung"
            icon="🌐"
            color="bg-green-50 border-green-200 text-green-800"
            items={[
              "Jedes Zeugnis: Original + beglaubigte deutsche Übersetzung",
              "Übersetzung von einem staatlich zertifizierten Dolmetscher",
            ]}
          />
          <DocCheckCard
            title="Chronologische Reihenfolge im PDF"
            icon="📑"
            color="bg-purple-50 border-purple-200 text-purple-800"
            items={[
              "Reihenfolge: Master → Bachelor → HSC → SSC",
              "Pro Abschluss: Zeugnis (Übersetzt) + Zeugnis (Original) + Notenspiegel (Übersetzt) + Notenspiegel (Original)",
              "Danach: Sprachzeugnis + weitere Nachweise",
            ]}
          />
        </div>
        <div className="mt-3 bg-gray-900 rounded-xl px-4 py-3 text-xs font-mono">
          <p className="text-gray-400 mb-1 text-[10px] uppercase tracking-wide">
            Reihenfolge im Zeugnisse-PDF:
          </p>
          <div className="space-y-0.5">
            {[
              { text: "Master-Zeugnis (Übersetzt)", color: "text-[#009fe3]" },
              { text: "Master-Zeugnis (Original)", color: "text-[#009fe3]" },
              {
                text: "Master-Notenspiegel (Übersetzt)",
                color: "text-blue-300",
              },
              {
                text: "Master-Notenspiegel (Original)",
                color: "text-blue-300",
              },
              { text: "Bachelor-Zeugnis (Übersetzt)", color: "text-green-400" },
              { text: "Bachelor-Zeugnis (Original)", color: "text-green-400" },
              { text: "... (HSC, SSC gleich)", color: "text-gray-500" },
              { text: "Sprachzeugnis (B2)", color: "text-amber-400" },
            ].map((row, i) => (
              <div key={i} className={`flex items-center gap-2 ${row.color}`}>
                <span className="text-gray-600">
                  {String(i + 1).padStart(2, "0")}.
                </span>
                <span>{row.text}</span>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 02 — Qualitätscheck LL + Keywords */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="✅"
        title={"Qualitätscheck — Stellenausschreibungen & 3-Lebenslauf-Check"}
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-2">
              🔍 Stellenausschreibungen verstehen
            </p>
            <div className="flex items-start gap-2 text-sm text-blue-700">
              <span className="flex-shrink-0 mt-0.5">›</span>
              <span>
                Hast du die <strong>Must-have-Anforderungen</strong> und{" "}
                <strong>Keywords</strong> aus der Anzeige vollständig verstanden
                und markiert?
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm font-bold text-green-800 mb-2">
              📄 Der 3-Lebenslauf-Check
            </p>
            <div className="space-y-1.5">
              {[
                "Hast du drei Versionen erstellt, die unterschiedliche Schwerpunkte deiner Stärken betonen?",
                "Ist jede Version exakt auf die jeweilige Stellenanzeige zugeschnitten?",
                "Spiegeln die Keywords aus der Anzeige den Lebenslauf wider?",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-sm text-green-700"
                >
                  <span className="flex-shrink-0 mt-0.5 font-bold">
                    {i + 1}.
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <InfoBox variant="tip" icon="💡" title="Merksatz">
          Ein Lebenslauf für alle Stellen funktioniert nicht. Jede Stelle
          verdient eine eigene Version.
        </InfoBox>
      </Accordion>

      {/* 03 — Portal-Sprung */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="🚀"
        title="Portal-Sprung — Direkt zur Quelle"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Viele bekannte Portale sind nur <strong>Zwischenhändler</strong> —
          nutze sie zum Suchen, aber bewirb dich direkt bei der Firma.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🔗</span>
              <span className="text-sm font-bold text-orange-800">
                Portale = Zwischenhändler
              </span>
            </div>
            <p className="text-xs text-orange-700">
              Portale wie{" "}
              <a
                href="https://www.ausbildung.de"
                target="_blank"
                rel="noreferrer"
                className="underline font-semibold"
              >
                ausbildung.de
              </a>{" "}
              oder{" "}
              <a
                href="https://www.arbeitsagentur.de"
                target="_blank"
                rel="noreferrer"
                className="underline font-semibold"
              >
                arbeitsagentur.de
              </a>{" "}
              leiten deine Bewerbung nur weiter — mit Zeitverzug und weniger
              Infos.
            </p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🎯</span>
              <span className="text-sm font-bold text-green-800">
                Warum direkt besser ist
              </span>
            </div>
            <ul className="space-y-1 text-xs text-green-700">
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Deine Bewerbung landet
                ohne Umwege direkt im HR-System der Firma
              </li>
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Du siehst oft
                zusätzliche Teaminfos und aktuelle Stellenbeschreibungen
              </li>
              <li className="flex gap-1.5">
                <span className="flex-shrink-0">›</span>Schnellere Bearbeitung,
                professionellerer Eindruck
              </li>
            </ul>
          </div>
        </div>
        <InfoBox variant="info" icon="ℹ️" title="Strategie">
          Nutze Portale zum <strong>Suchen und Finden</strong> von Stellen. Für
          die Bewerbung selbst dann direkt auf die Firmen-Website wechseln.
        </InfoBox>
      </Accordion>

      {/* 04 — Portal-Sprung Trick */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🔧"
        title="Portal-Sprung — Der Trick"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          So findest du die direkte Bewerbungsseite einer Firma in 3 Schritten:
        </p>
        <div className="space-y-2">
          {(
            [
              {
                step: "1",
                icon: "📋",
                label: "Firmennamen kopieren",
                desc: 'Kopiere den exakten Firmennamen aus dem Portal (z.B. "HEK - Hanseatische Krankenkasse")',
                cls: "bg-blue-50 border-blue-200 text-blue-800",
              },
              {
                step: "2",
                icon: "🔍",
                label: "Suchmaschine nutzen",
                desc: 'Gib den Firmennamen + "Karriere" oder "Ausbildung" in Google ein, um die offizielle Homepage zu finden',
                cls: "bg-purple-50 border-purple-200 text-purple-800",
              },
              {
                step: "3",
                icon: "💼",
                label: "Karriere-Seite suchen",
                desc: 'Suche auf der Firmenhomepage nach dem Reiter "Karriere", "Jobs" oder "Ausbildung" und bewirb dich dort direkt',
                cls: "bg-green-50 border-green-200 text-green-800",
              },
            ] as {
              step: string;
              icon: string;
              label: string;
              desc: string;
              cls: string;
            }[]
          ).map((item) => (
            <div
              key={item.step}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${item.cls}`}
            >
              <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <p className="text-sm font-bold">
                  {item.icon} {item.label}
                </p>
                <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-gray-900 rounded-xl px-4 py-3 text-xs">
          <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5">
            Beispiel Google-Suche:
          </p>
          <code className="text-green-400">
            HEK Hanseatische Krankenkasse Ausbildung Karriere
          </code>
          <p className="text-gray-500 mt-2 text-[10px]">
            → Führt direkt zur Karriere-Seite der HEK, nicht zum Portal
          </p>
        </div>
      </Accordion>

      {/* 05 — G2G Portal */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="🤖"
        title="Anschreiben generieren mit dem G2G-Portal"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Effizienz ist der Schlüssel. Das G2G-Portal hilft dir, individuelle
          Anschreiben schnell und professionell zu erstellen.
        </p>
        <div className="space-y-3">
          {[
            {
              icon: "📥",
              step: "01",
              label: "Input-Qualität",
              desc: "Füttere das Portal mit den spezifischen Anforderungen der Stellenanzeige — Keywords, Aufgaben, Anforderungen.",
              tip: "Je mehr Details du eingibst, desto besser der Output.",
              color: "bg-blue-50 border-blue-200",
              tipColor: "text-blue-600",
            },
            {
              icon: "🔗",
              step: "02",
              label: "Personalisierung",
              desc: "Nutze das Portal, um eine Brücke zwischen deinem Lebenslauf und der Firma zu bauen — deine Stärken auf die Stelle zugeschnitten.",
              tip: "Importiere deinen Lebenslauf und die Anzeige zusammen.",
              color: "bg-purple-50 border-purple-200",
              tipColor: "text-purple-600",
            },
            {
              icon: "✏️",
              step: "03",
              label: "Der Feinschliff",
              desc: "Ein generierter Text ist fast fertig. Nur wenn nötig: selbst ergänzen oder anpassen. G2G prüft danach nochmals.",
              tip: "Verändere nicht zu viel — das Portal optimiert bereits für B2-Niveau.",
              color: "bg-green-50 border-green-200",
              tipColor: "text-green-600",
            },
          ].map((item) => (
            <div
              key={item.step}
              className={`rounded-xl border p-3 ${item.color}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-full bg-white/70 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                  {item.step}
                </div>
                <span className="text-sm font-bold text-gray-800">
                  {item.icon} {item.label}
                </span>
              </div>
              <p className="text-xs text-gray-600 pl-8">{item.desc}</p>
              <p className={`text-[11px] italic pl-8 mt-1 ${item.tipColor}`}>
                💡 {item.tip}
              </p>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 06 — Ordnerstruktur */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="📁"
        title="Digitale Ablage — Ordnerstruktur"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm font-semibold text-gray-700 mb-1">
          Ordnung ist der halbe Job.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Erstelle eine professionelle Ordnerstruktur — so findest du jede
          Bewerbung sofort wieder.
        </p>

        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-3">
            <p className="text-xs font-bold text-[#0369a1] uppercase tracking-wide mb-1">
              Hauptordner
            </p>
            <code className="text-sm font-mono font-bold text-[#0369a1]">
              Bewerbungen_2026
            </code>
            <p className="text-xs text-[#0369a1] opacity-70 mt-1">
              Ein Ordner für alle Bewerbungen dieses Jahres
            </p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">
              Unterordner pro Stelle
            </p>
            <code className="text-sm font-mono font-bold text-green-800">
              JJJJ-MM-TT_Firmenname_Berufswunsch
            </code>
            <div className="mt-2 bg-white rounded-lg px-3 py-2 border border-green-200">
              <p className="text-[10px] text-green-600 uppercase tracking-wide mb-0.5">
                Beispiel:
              </p>
              <code className="text-xs font-mono text-green-800 font-bold">
                2026-02-07_HEK_KaufmannGesundheitswesen
              </code>
            </div>
            <p className="text-xs text-green-600 mt-2 opacity-80">
              Das Datum vorne sorgt für automatische chronologische Sortierung.
            </p>
          </div>
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
          Beispiel Ordnerstruktur:
        </p>
        <FolderTree />
      </Accordion>

      {/* 07 — Ordnerinhalt */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="📂"
        title="Digitale Ablage — Inhalte eines Bewerbungsordners"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Jeder Bewerbungsordner braucht diese 5 Dateien:
        </p>
        <div className="space-y-2">
          {(
            [
              {
                icon: "📄",
                label: "Stellenanzeige als PDF",
                desc: "Anzeigen werden nach Ablauf der Frist oft gelöscht — du brauchst sie zur Vorbereitung auf das Vorstellungsgespräch.",
                tip: 'Strg + P im Browser → "Als PDF speichern" wählen',
                highlight: true,
                cls: "bg-red-50 border-red-200 text-red-800",
              },
              {
                icon: "🔗",
                label: "Anzeige-Link als .txt-Datei",
                desc: "Kopiere den Link der Anzeige in eine einfache Textdatei und speichere sie im Ordner.",
                tip: "Für schnellen Zugriff auf die Original-URL",
                highlight: false,
                cls: "bg-orange-50 border-orange-200 text-orange-800",
              },
              {
                icon: "👤",
                label: "Versandter Lebenslauf",
                desc: "Exakt die Version, die du abgeschickt hast — nicht eine neuere Version.",
                tip: "Dateiname: Lebenslauf_Firmenname_v1.pdf",
                highlight: false,
                cls: "bg-blue-50 border-blue-200 text-blue-800",
              },
              {
                icon: "✉️",
                label: "Anschreiben",
                desc: "Das fertige Anschreiben, das du abgeschickt hast.",
                tip: "Dateiname: Anschreiben_Firmenname.pdf",
                highlight: false,
                cls: "bg-purple-50 border-purple-200 text-purple-800",
              },
              {
                icon: "📦",
                label: "Komplett-PDF (merged)",
                desc: "Anzeige + Lebenslauf + Anschreiben in einem PDF zusammengeführt — für die Vorbereitung auf das Vorstellungsgespräch.",
                tip: null,
                highlight: true,
                cls: "bg-green-50 border-green-200 text-green-800",
              },
            ] as {
              icon: string;
              label: string;
              desc: string;
              tip: string | null;
              highlight: boolean;
              cls: string;
            }[]
          ).map((item, i) => (
            <div key={i} className={`rounded-xl border px-3 py-3 ${item.cls}`}>
              <div className="flex items-start gap-2.5">
                <span className="text-base flex-shrink-0">{item.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-bold">{item.label}</p>
                  <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
                  {item.tip && (
                    <p className="text-[11px] italic opacity-60 mt-1">
                      💡 {item.tip}
                    </p>
                  )}
                  {item.label.includes("merged") ||
                  item.label.includes("Komplett") ? (
                    <a
                      href="https://www.ilovepdf.com/merge_pdf"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-1.5 text-[11px] font-bold underline opacity-80 hover:opacity-100"
                    >
                      🔗 ilovepdf.com/merge_pdf
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
        <InfoBox
          variant="warning"
          icon="⚠️"
          title="Wichtig — Anzeigen werden gelöscht!"
        >
          Speichere die Stellenanzeige als PDF, <strong>bevor</strong> du die
          Bewerbung abschickst. Nach Ablauf der Frist ist sie oft nicht mehr
          abrufbar — du brauchst sie aber zur Vorbereitung auf das
          Vorstellungsgespräch.
        </InfoBox>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Organisation ist genauso wichtig wie der Inhalt der Bewerbung.
            </p>
            <ul className="space-y-1">
              {[
                "Alle Zeugnisse chronologisch als sauberes PDF — Original + Übersetzung",
                "3 Lebensläufe individuell auf jede Anzeige zugeschnitten",
                "Portal-Sprung: Über Portale suchen, direkt bei Firmen bewerben",
                "G2G-Portal für effiziente, professionelle Anschreiben nutzen",
                "Ordnerstruktur: JJJJ-MM-TT_Firma_Beruf für jede Bewerbung",
                "5 Dateien pro Ordner: Anzeige · Link · Lebenslauf · Anschreiben · Komplett-PDF",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 05"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              "3 Lebensläufe und passende Anschreiben vorbereiten (je Stellenanzeige eine Version)",
              <>
                Ordnerstruktur erstellen:{" "}
                <code className="bg-gray-100 px-1 rounded text-xs">
                  Bewerbungen_2026
                </code>{" "}
                mit Unterordnern nach Schema{" "}
                <code className="bg-gray-100 px-1 rounded text-xs">
                  JJJJ-MM-TT_Firma_Beruf
                </code>
              </>,
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture05Unterlagenkontrolle;
